import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import {
  setOccurrenceStatusSchema,
  type SetOccurrenceStatusInput,
  type TaskStatus,
} from '@pms/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { VoiceNoteRecorder, type VoiceNoteDraft } from './VoiceNoteRecorder';

/** Statuses this modal collects a remark for, and how each one is worded. */
type RemarkStatus = Extract<TaskStatus, 'discarded' | 'needs_improvement'>;

const COPY: Record<RemarkStatus, {
  title: string; description: string; label: string; placeholder: string;
  confirm: string; destructive: boolean;
}> = {
  discarded: {
    title: 'Discard Task',
    description: 'Discarding closes this task without completing it. Record why.',
    label: 'Remark',
    placeholder: 'Why is this task being discarded?',
    confirm: 'Discard Task',
    destructive: true,
  },
  needs_improvement: {
    title: 'Send Back for Improvement',
    description: 'The task returns to the assignee, who can update it and submit again.',
    label: 'What needs improving?',
    placeholder: 'Tell the assignee what to change…',
    confirm: 'Send Back',
    destructive: false,
  },
};

interface StatusRemarkModalProps {
  /** Which status is being set — decides the wording and the validation branch. */
  status: RemarkStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `voice` is null unless the reviewer recorded one; only Send Back offers it. */
  onConfirm: (note: string, voice: VoiceNoteDraft | null) => void;
  busy?: boolean;
}

/**
 * Collects the mandatory remark for the two statuses that need one.
 *
 * Both close a door on someone else's work, so both owe an explanation. Validation
 * resolves against the shared `setOccurrenceStatusSchema` with `status` pinned, so
 * required-ness and its wording come from the same schema the API enforces — the rule is
 * never restated in two places.
 */
export function StatusRemarkModal({
  status, open, onOpenChange, onConfirm, busy = false,
}: StatusRemarkModalProps) {
  const copy = COPY[status];
  // Only Send Back offers audio. Discarding is a closing action, not a conversation.
  const allowsVoice = status === 'needs_improvement';
  const [voiceNote, setVoiceNote] = useState<VoiceNoteDraft | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SetOccurrenceStatusInput>({
    resolver: zodResolver(setOccurrenceStatusSchema),
    defaultValues: { status, note: '' },
  });

  // Re-pin the status so a modal reused for a different action validates against the
  // right branch. The voice draft needs no reset here: every caller mounts this only
  // while it is open, so each opening starts from a fresh component.
  useEffect(() => {
    if (open) reset({ status, note: '' });
  }, [open, status, reset]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onOpenChange(false); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((v) => onConfirm((v.note ?? '').trim(), allowsVoice ? voiceNote : null))}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="status-remark">
              {copy.label} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="status-remark"
              rows={3}
              placeholder={copy.placeholder}
              className={errors.note ? 'resize-none border-destructive' : 'resize-none'}
              {...register('note')}
            />
            {errors.note && <p className="text-xs text-destructive">{errors.note.message}</p>}
          </div>

          {/* The written remark stays mandatory — this is an addition, not a substitute,
              so the assignee always has something readable to work from. */}
          {allowsVoice && (
            <VoiceNoteRecorder value={voiceNote} onChange={setVoiceNote} disabled={busy} />
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant={copy.destructive ? 'destructive' : 'default'} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {copy.confirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
